import {
	createQrImageBuilder,
	getQrDataToEncode,
	TQrDesign,
} from '@/generator/client'
import { TQrData } from '@/generator/shared'

import { buildMobiUrl } from './urlUtils'

export type DOWNLOAD_QR_ACTION_TYPES =
	| 'downloadAsSvg'
	| 'downloadAsPng'
	| 'downloadAsJpg'

const extensions: Record<DOWNLOAD_QR_ACTION_TYPES, string> = {
	downloadAsSvg: 'svg',
	downloadAsPng: 'png',
	downloadAsJpg: 'jpeg',
}

const downloadBlob = (blob: Blob, filename: string) => {
	const url = URL.createObjectURL(blob)
	const link = document.createElement('a')
	link.href = url
	link.download = filename
	link.click()
	URL.revokeObjectURL(url)
}

const getSvgDimensions = (svg: string) => {
	const svgDoc = new DOMParser().parseFromString(svg, 'image/svg+xml')
	const svgEl = svgDoc.querySelector('svg')
	const width = svgEl?.getAttribute('width')
		? parseInt(svgEl.getAttribute('width')!)
		: 320
	const height = svgEl?.getAttribute('height')
		? parseInt(svgEl.getAttribute('height')!)
		: 320
	return { width, height }
}

const renderSvgToCanvas = (
	svg: string,
	type: 'png' | 'jpeg',
): Promise<HTMLCanvasElement> => {
	return new Promise((resolve, reject) => {
		const img = new Image()
		const svgBlob = new Blob([svg], { type: 'image/svg+xml' })
		const url = URL.createObjectURL(svgBlob)

		img.onload = () => {
			const { width, height } = getSvgDimensions(svg)
			const deviceRatio = window?.devicePixelRatio || 1
			const scaleFactor = deviceRatio * 2

			const canvas = document.createElement('canvas')
			canvas.width = width * scaleFactor
			canvas.height = height * scaleFactor

			const ctx = canvas.getContext('2d')!
			if (type === 'jpeg') {
				ctx.fillStyle = '#ffffff'
				ctx.fillRect(0, 0, canvas.width, canvas.height)
			}

			ctx.scale(scaleFactor, scaleFactor)
			ctx.drawImage(img, 0, 0, width, height)

			URL.revokeObjectURL(url)
			resolve(canvas)
		}

		img.onerror = () => {
			URL.revokeObjectURL(url)
			reject(new Error('Failed to load QR code image for rendering'))
		}

		img.src = url
	})
}

const canvasToBlob = (
	canvas: HTMLCanvasElement,
	type: 'png' | 'jpeg',
): Promise<Blob> => {
	return new Promise((resolve, reject) => {
		canvas.toBlob(
			(blob) => {
				if (blob) {
					resolve(blob)
				} else {
					reject(new Error('Failed to generate QR code image'))
				}
			},
			`image/${type}`,
			0.95,
		)
	})
}

export const buildQrMarkup = async (qrCode: TQrData) => {
	const dataToEncode = getQrDataToEncode({
		qrUri: qrCode.uri,
		qrContent: qrCode.content,
	})
	const qrDesign = { ...qrCode.qrDesign, data: dataToEncode }
	const qrImageBuilder = await createQrImageBuilder({
		...qrDesign,
	} as Parameters<typeof createQrImageBuilder>[0])
	const finalSVG = await qrImageBuilder.getOptimizedSvg()
	return finalSVG
}

export const buildQrMarkupWithoutFrame = async (qrCode: TQrData) => {
	const dataToEncode = buildMobiUrl(qrCode.uri)
	const qrDesignWithoutFrame = {
		...qrCode.qrDesign,
		frame: { type: 'default' },
	}
	const qrDesign = { ...qrDesignWithoutFrame, data: dataToEncode }
	const builder = await createQrImageBuilder(qrDesign as TQrDesign)
	const finalSVG = await builder.getPatternSvg()
	return finalSVG
}

export const downloadQRCode = async (
	qrCode: TQrData,
	extension: DOWNLOAD_QR_ACTION_TYPES,
) => {
	const type = extensions[extension]
	const finalSVG = await buildQrMarkup(qrCode)

	if (extension === 'downloadAsSvg') {
		downloadBlob(
			new Blob([finalSVG], { type: 'image/svg+xml' }),
			`${qrCode.id}.svg`,
		)
		return
	}

	const canvas = await renderSvgToCanvas(finalSVG, type as 'png' | 'jpeg')
	const blob = await canvasToBlob(canvas, type as 'png' | 'jpeg')
	downloadBlob(blob, `${qrCode.id}.${type}`)
}
